import React from "react"
import { 
  DevicePhoneMobileIcon, 
  ComputerDesktopIcon, 
  EyeIcon 
} from "@heroicons/react/24/outline"
import { getPlatformIcon } from "aldoo-ra/Admin/ProductLicensing/PlatformIcons"

/**
 * Central utility function for rendering platform icons consistently across admin pages
 * Based on the logic from PlatformDevices.jsx PlatformBar component
 * @param {string} platform - Platform name
 * @param {string} className - CSS classes for the icon
 * @returns {JSX.Element} - Platform-specific icon component
 */
export const renderPlatformIcon = (platform, className = "w-5 h-5 text-admin_text_secondary") => {
  if (!platform) {
    return <DevicePhoneMobileIcon className={className} />
  }

  const platformLower = platform.toLowerCase()
  
  // Check for platforms that need custom icons first
  if (platformLower.includes('server')) {
    return <ComputerDesktopIcon className={className} />
  } else if (platformLower.includes('development') || platformLower.includes('dev')) {
    return <EyeIcon className={className} />
  } else if (platformLower.includes('mobile')) {
    return <DevicePhoneMobileIcon className={className} />
  }
  
  // For known platforms, try to get the specific brand icon
  const knownPlatforms = ['ios', 'android', 'windows', 'macos', 'mac', 'linux', 'web']
  const matchedPlatform = knownPlatforms.find(p => platformLower.includes(p))
  
  if (matchedPlatform) {
    try {
      const icon = getPlatformIcon(platformLower, className)
      if (icon) {
        return icon
      }
    } catch (error) {
      console.error(`[renderPlatformIcon] Failed to get platform icon for "${platform}":`, error)
      // Fall through to default
    }
  }
  
  // Generic fallback for any other unknown platform
  return <ComputerDesktopIcon className={className} />
}

/**
 * Get platform display name with proper casing
 * @param {string} platform - Platform name
 * @returns {string} - Formatted platform name
 */
export const getPlatformDisplayName = (platform) => {
  if (!platform) return "Unknown"
  
  const platformLower = platform.toLowerCase()
  
  // Handle special cases
  if (platformLower.includes('ios')) return "iOS"
  if (platformLower.includes('android')) return "Android"
  if (platformLower.includes('macos') || platformLower.includes('mac')) return "macOS"
  if (platformLower.includes('windows')) return "Windows"
  if (platformLower.includes('linux')) return "Linux"
  if (platformLower.includes('web')) return "Web"
  
  // Capitalize first letter for other platforms
  return platform.charAt(0).toUpperCase() + platform.slice(1).toLowerCase()
}

export default {
  renderPlatformIcon,
  getPlatformDisplayName
}